export interface DisputeReasonInfo {
  label: string;
  description?: string;
  badgeColor?: string;
}

const REASON_DICTIONARY: Record<string, DisputeReasonInfo> = {
  // Classification keys
  'ADJUSTMENT_ALLOCATION': {
    label: 'Calculated Tip Discrepancy',
    description: 'Calculated tip amount differs from expected shift allocation',
    badgeColor: 'bg-blue-50 text-blue-700 border-blue-200'
  },
  'DATA_GAP_SCHEDULE': {
    label: 'Missing from Schedule',
    description: 'Staff worked shift but was omitted from the schedule or roster',
    badgeColor: 'bg-amber-50 text-amber-700 border-amber-200'
  },
  'ADJUSTMENT_HOURS': {
    label: 'Shift Hours Inaccurate',
    description: 'Hours worked for this shift were recorded incorrectly',
    badgeColor: 'bg-purple-50 text-purple-700 border-purple-200'
  },
  'DATA_GAP_SHIFT': {
    label: 'Missing Shift Entry',
    description: 'Entire shift was not recorded in the tip pool',
    badgeColor: 'bg-rose-50 text-rose-700 border-rose-200'
  },
  'ALLOCATION_CORRECTION': {
    label: 'Tip Allocation Correction',
    description: 'Correction requested for pooled tip distribution',
    badgeColor: 'bg-indigo-50 text-indigo-700 border-indigo-200'
  },
  'Allocation-Correction': {
    label: 'Tip Allocation Correction',
    description: 'Correction requested for pooled tip distribution',
    badgeColor: 'bg-indigo-50 text-indigo-700 border-indigo-200'
  },
  'SUBMISSION_CORRECTION': {
    label: 'Tip Submission Correction',
    description: 'Correction to initial tip amount recorded',
    badgeColor: 'bg-teal-50 text-teal-700 border-teal-200'
  },
  'Submission-Correction': {
    label: 'Tip Submission Correction',
    description: 'Correction to initial tip amount recorded',
    badgeColor: 'bg-teal-50 text-teal-700 border-teal-200'
  },
  'BENCHMARK_INQUIRY': {
    label: 'Benchmark Trends Inquiry',
    description: 'General inquiry from benchmark comparisons',
    badgeColor: 'bg-sky-50 text-sky-700 border-sky-200'
  },
  'INCORRECT_ENTRY': {
    label: 'Data Entry Error',
    description: 'Incorrect number entered during shift checkout',
    badgeColor: 'bg-orange-50 text-orange-700 border-orange-200'
  },
  'POS_OVERRIDE': {
    label: 'POS Tip Override',
    description: 'Manual adjustment to POS transaction tip',
    badgeColor: 'bg-slate-50 text-slate-700 border-slate-200'
  }
};

/**
 * Formats a raw dispute reason or classification string into a clear, user-friendly label.
 */
export function formatDisputeReason(reason?: string | null): string {
  if (!reason) return 'General Review';

  const trimmed = reason.trim();
  const upper = trimmed.toUpperCase();

  if (REASON_DICTIONARY[trimmed]) {
    return REASON_DICTIONARY[trimmed].label;
  }
  if (REASON_DICTIONARY[upper]) {
    return REASON_DICTIONARY[upper].label;
  }

  // If it's a known snake_case or SCREAMING_SNAKE_CASE string, humanize it
  if (/^[A-Z0-9_-]+$/.test(trimmed) && (trimmed.includes('_') || trimmed.includes('-'))) {
    return trimmed
      .split(/[-_]+/)
      .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  }

  return trimmed;
}

/**
 * Returns complete reason metadata including label and helpful context description.
 */
export function getDisputeReasonInfo(reason?: string | null): DisputeReasonInfo {
  if (!reason) {
    return { label: 'General Review' };
  }

  const trimmed = reason.trim();
  const upper = trimmed.toUpperCase();

  if (REASON_DICTIONARY[trimmed]) {
    return REASON_DICTIONARY[trimmed];
  }
  if (REASON_DICTIONARY[upper]) {
    return REASON_DICTIONARY[upper];
  }

  return {
    label: formatDisputeReason(reason)
  };
}

const INQUIRY_CLASSIFICATIONS = new Set([
  'DATA_GAP_SCHEDULE',
  'DATA_GAP_SHIFT',
  'ADJUSTMENT_HOURS',
  'BENCHMARK_INQUIRY',
  'GENERAL_INQUIRY'
]);

/**
 * Determines whether a dispute item represents a Support/Benchmark/Informational Inquiry
 * rather than a structured ledger tip amount correction.
 */
export function isSupportInquiry(dispute: any): boolean {
  if (!dispute) return false;
  
  if (dispute.category === 'inquiry' || dispute.isSupportInquiry === true) {
    return true;
  }

  const reasonUpper = (dispute.reason || dispute.classification || '').toUpperCase().trim();
  if (INQUIRY_CLASSIFICATIONS.has(reasonUpper)) {
    return true;
  }

  // If reason contains benchmark inquiry or schedule gap
  if (
    reasonUpper.includes('BENCHMARK') || 
    reasonUpper.includes('SCHEDULE') || 
    reasonUpper.includes('HOURS') || 
    reasonUpper.includes('DATA_GAP') ||
    reasonUpper.includes('INQUIRY')
  ) {
    return true;
  }

  // If no monetary delta and reason indicates non-financial request
  const original = Number(dispute.originalAmount || 0);
  const corrected = Number(dispute.correctedAmount || 0);
  if (original === 0 && corrected === 0 && !dispute.masterTxId) {
    return true;
  }

  return false;
}

/**
 * Determines whether a dispute item is a structured financial tip correction.
 */
export function isFinancialDispute(dispute: any): boolean {
  return !isSupportInquiry(dispute);
}

